import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Handshake, Plus, Search, Building2, Users } from 'lucide-react';
import platformAdminService from '../../services/platformAdminService';
import ApiKeyRevealDialog from '../shared/ApiKeyRevealDialog';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { describeApiError, apiErrorText } from '../../utils/apiError';
import { PLATFORM_PARTNER_COPY, TENANCY_COPY } from '../../constants/partnerCopy';
import PaginationBar from './PaginationBar';
import PartnerDetail from './PartnerDetail';
import styles from './PlatformAdmin.module.scss';

const PAGE_SIZE = 50;

const EMPTY_FORM = { name: '', email: '', first_name: '', last_name: '' };

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
};

const plural = (n, one, many) => `${n ?? 0} ${Number(n) === 1 ? one : many}`;

/**
 * Platform → Partners (Partner tier guide §4.1–§4.2): the partner list, the
 * create form (with an optional first partner admin) and the drill-in detail.
 *
 * `onPartnersChanged` lets the console reload its partner index (used to label
 * home companies and fill Partner filters elsewhere).
 */
const PartnersPanel = ({ notify, catalog, partnerIndex, onPartnersChanged }) => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 400);
  const [activeOnly, setActiveOnly] = useState(false);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);

  const [selected, setSelected] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [creating, setCreating] = useState(false);
  const [keyReveal, setKeyReveal] = useState(null);

  const loadPartners = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await platformAdminService.listPartners({
        activeOnly,
        search: debouncedSearch || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      const list = data?.partners || (Array.isArray(data) ? data : []);
      setPartners(list);
      setTotal(Number.isFinite(data?.total) ? data.total : list.length);
    } catch (err) {
      setError(describeApiError(err, 'Failed to load partners.'));
    } finally {
      setLoading(false);
    }
  }, [activeOnly, debouncedSearch, offset]);

  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  const refreshAll = useCallback(async () => {
    await loadPartners();
    onPartnersChanged?.();
  }, [loadPartners, onPartnersChanged]);

  const closeCreate = () => {
    if (creating) return;
    setCreateOpen(false);
    setForm(EMPTY_FORM);
    setFieldErrors({});
  };

  const handleCreate = async () => {
    const name = form.name.trim();
    if (!name) return;
    const email = form.email.trim();
    const payload = { name };
    if (email) {
      payload.first_admin = {
        email,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
      };
    }
    try {
      setCreating(true);
      setFieldErrors({});
      const res = await platformAdminService.createPartner(payload);
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      notify('Partner created.', 'success');
      if (res?.first_admin?.api_key) {
        setKeyReveal({
          apiKey: res.first_admin.api_key,
          roles: res.first_admin.roles || [],
          title: 'Partner created',
          subtitle: `Copy ${res.first_admin.user?.email || 'the first admin'}'s API key now — it won't be shown again.`,
        });
      }
      await refreshAll();
      if (res?.partner) setSelected(res.partner);
    } catch (err) {
      const text = apiErrorText(err);
      if (err?.status === 400 && /partner named .* already exists/i.test(text)) {
        setFieldErrors({ name: 'A partner with this name already exists.' });
      } else if (err?.status === 400 && /user with that email already exists/i.test(text)) {
        setFieldErrors({
          email:
            'This email already has an account. Add it to the partner from Partner → Users instead.',
        });
      } else if (err?.status >= 500 && payload.first_admin) {
        // The partner row may already exist even though its first admin failed.
        setCreateOpen(false);
        setForm(EMPTY_FORM);
        notify(PLATFORM_PARTNER_COPY.firstAdminFailed, 'warning');
        await refreshAll();
      } else {
        notify(describeApiError(err, 'Failed to create partner.'), 'error');
      }
    } finally {
      setCreating(false);
    }
  };

  // Rendered in both views: a new partner opens straight into its detail page
  // while its first admin's key is still waiting to be copied.
  const revealDialog = (
    <ApiKeyRevealDialog
      open={!!keyReveal}
      apiKey={keyReveal?.apiKey}
      roles={keyReveal?.roles}
      title={keyReveal?.title}
      subtitle={keyReveal?.subtitle}
      onClose={() => setKeyReveal(null)}
    />
  );

  if (selected) {
    return (
      <>
        <PartnerDetail
          partner={selected}
          catalog={catalog}
          partnerIndex={partnerIndex}
          notify={notify}
          onBack={() => setSelected(null)}
          onChanged={refreshAll}
        />
        {revealDialog}
      </>
    );
  }

  return (
    <>
      <Box className={styles.toolbar}>
        <Box className={styles.filterGroup}>
          <TextField
            size="small"
            placeholder="Search partners…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
            sx={{ minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={activeOnly ? 'active' : 'all'}
              onChange={(e) => { setActiveOnly(e.target.value === 'active'); setOffset(0); }}
            >
              <MenuItem value="all">All partners</MenuItem>
              <MenuItem value="active">Active only</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Button
          variant="contained"
          startIcon={<Plus size={18} />}
          className={styles.primaryBtn}
          onClick={() => setCreateOpen(true)}
        >
          New partner
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ borderRadius: '12px' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : partners.length === 0 ? (
        <Box className={styles.emptyState}>
          <Handshake className={styles.emptyIcon} size={56} />
          <Typography variant="h6">No partners yet</Typography>
          <Typography variant="body2">
            A partner is a reseller or consultant that brings its own client companies onto the platform.
          </Typography>
        </Box>
      ) : (
        <Box className={styles.tableCard}>
          {partners.map((partner) => (
            <Box
              key={partner.id}
              className={styles.companyRow}
              onClick={() => setSelected(partner)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && setSelected(partner)}
              sx={{ opacity: partner.is_active ? 1 : 0.6 }}
            >
              <Box className={styles.companyMain}>
                <span className={styles.companyAvatar}>
                  {(partner.name || '?').charAt(0).toUpperCase()}
                </span>
                <Box>
                  <span className={styles.companyName}>{partner.name}</span>
                  <span className={styles.companyMeta}>Created {formatDate(partner.created_at)}</span>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <span className={styles.badge}>
                  <Building2 size={12} />
                  {plural(partner.company_count, 'company', 'companies')}
                </span>
                <span className={styles.badge}>
                  <Users size={12} />
                  {plural(partner.user_count, 'user', 'users')}
                </span>
                <span className={`${styles.statusPill} ${partner.is_active ? styles.active : styles.inactive}`}>
                  {partner.is_active ? 'Active' : TENANCY_COPY.deactivated}
                </span>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {total > 0 && (
        <PaginationBar
          total={total}
          limit={PAGE_SIZE}
          offset={offset}
          loading={loading}
          unit="partners"
          onOffsetChange={setOffset}
        />
      )}

      {/* Create partner */}
      <Dialog open={createOpen} onClose={closeCreate} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>New partner</DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <TextField
            autoFocus
            label="Partner name"
            required
            fullWidth
            variant="outlined"
            value={form.name}
            onChange={(e) => { setForm({ ...form, name: e.target.value }); setFieldErrors({ ...fieldErrors, name: undefined }); }}
            error={!!fieldErrors.name}
            helperText={fieldErrors.name}
            className={styles.formField}
          />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {PLATFORM_PARTNER_COPY.firstAdminSection}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Recommended. They can then add the partner's companies and users themselves.
            </Typography>
          </Box>
          <TextField
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={form.email}
            onChange={(e) => { setForm({ ...form, email: e.target.value }); setFieldErrors({ ...fieldErrors, email: undefined }); }}
            error={!!fieldErrors.email}
            helperText={fieldErrors.email}
            className={styles.formField}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="First name"
              fullWidth
              variant="outlined"
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              disabled={!form.email.trim()}
              className={styles.formField}
            />
            <TextField
              label="Last name"
              fullWidth
              variant="outlined"
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              disabled={!form.email.trim()}
              className={styles.formField}
            />
          </Box>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={closeCreate} disabled={creating} className={styles.cancelButton}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || !form.name.trim()}
            className={styles.primaryBtn}
          >
            {creating ? <CircularProgress size={20} color="inherit" /> : 'Create partner'}
          </Button>
        </DialogActions>
      </Dialog>

      {revealDialog}
    </>
  );
};

export default PartnersPanel;
